import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, Users, Award, Download, Eye, Plus, Minus } from 'lucide-react';
import pointsService from '../services/pointsService';
import './PointsAnalytics.css';

const PointsAnalytics = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dashboard, setDashboard] = useState(null);
    const [topUsers, setTopUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(() => ({ startDate: searchParams.get('start') || '', endDate: searchParams.get('end') || '' }));
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [adjustmentModal, setAdjustmentModal] = useState(false);
    const [adjustment, setAdjustment] = useState({ points: '', reason: '', type: 'add' });

    useEffect(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (dateRange.startDate) next.set('start', dateRange.startDate); else next.delete('start');
            if (dateRange.endDate) next.set('end', dateRange.endDate); else next.delete('end');
            return next;
        }, { replace: true });
        fetchDashboard();
        fetchTopUsers();
    }, [dateRange, setSearchParams]);

    const fetchDashboard = async () => {
        try {
            const response = await pointsService.getAnalyticsDashboard(dateRange);
            if (response.success) {
                setDashboard(response.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTopUsers = async () => {
        try {
            const response = await pointsService.getTopUsers(10, 'earned');
            if (response.success) {
                setTopUsers(response.data.users);
            }
        } catch (error) {
            console.error('Error fetching top users:', error);
        }
    };

    const fetchUserDetails = async (userId) => {
        try {
            const response = await pointsService.getUserPoints(userId);
            if (response.success) {
                setUserDetails(response.data);
                setSelectedUser(userId);
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    const formatTransactionDate = (transaction) => {
        const raw = transaction?.created_at || transaction?.createdAt;
        if (!raw) return '—';
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const handleExport = async () => {
        try {
            const response = await pointsService.exportReport(dateRange);
            // Trigger download
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `points-report-${Date.now()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting report:', error);
            alert('Failed to export report');
        }
    };

    const handleAdjustPoints = async () => {
        if (!selectedUser || !adjustment.points || !adjustment.reason) {
            alert('Please fill all fields');
            return;
        }

        try {
            const points = adjustment.type === 'add'
                ? parseInt(adjustment.points)
                : -parseInt(adjustment.points);

            const response = await pointsService.adjustPoints({
                user_id: selectedUser,
                points,
                reason: adjustment.reason,
                adjustment_type: 'manual_adjustment'
            });

            if (response.success) {
                alert('Points adjusted successfully');
                setAdjustmentModal(false);
                setAdjustment({ points: '', reason: '', type: 'add' });
                fetchUserDetails(selectedUser);
                fetchDashboard();
            }
        } catch (error) {
            console.error('Error adjusting points:', error);
            alert(error.message || 'Failed to adjust points');
        }
    };

    if (loading) {
        return (
            <div className="points-analytics-loading">
                <div className="spinner"></div>
                <p>Loading analytics...</p>
            </div>
        );
    }

    return (
        <div className="points-analytics">
            <div className="analytics-header">
                <h1>Points Analytics</h1>
                <div className="header-actions">
                    <div className="date-range">
                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                            placeholder="Start Date"
                        />
                        <span>to</span>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                            placeholder="End Date"
                        />
                    </div>
                    <button className="export-btn" onClick={handleExport}>
                        <Download size={18} />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Dashboard Widgets */}
            <div className="dashboard-widgets">
                <div className="widget">
                    <div className="widget-icon" style={{ backgroundColor: '#10b98115' }}>
                        <TrendingUp size={24} color="#10b981" />
                    </div>
                    <div className="widget-content">
                        <p className="widget-label">Total Points Issued</p>
                        <h2 className="widget-value">{dashboard?.totalIssued?.toLocaleString() || 0}</h2>
                    </div>
                </div>

                <div className="widget">
                    <div className="widget-icon" style={{ backgroundColor: '#FF950015' }}>
                        <TrendingDown size={24} color="#FF9500" />
                    </div>
                    <div className="widget-content">
                        <p className="widget-label">Total Points Redeemed</p>
                        <h2 className="widget-value">{dashboard?.totalRedeemed?.toLocaleString() || 0}</h2>
                    </div>
                </div>

                <div className="widget">
                    <div className="widget-icon" style={{ backgroundColor: '#007AFF15' }}>
                        <Award size={24} color="#007AFF" />
                    </div>
                    <div className="widget-content">
                        <p className="widget-label">Redemption Rate</p>
                        <h2 className="widget-value">{dashboard?.redemptionRate || 0}%</h2>
                    </div>
                </div>

                <div className="widget">
                    <div className="widget-icon" style={{ backgroundColor: '#d0253315' }}>
                        <Users size={24} color="#d02533" />
                    </div>
                    <div className="widget-content">
                        <p className="widget-label">Avg Points/Order</p>
                        <h2 className="widget-value">{dashboard?.avgPointsPerOrder || 0}</h2>
                    </div>
                </div>
            </div>

            {/* Top Users Table */}
            <div className="top-users-section">
                <h2>Top Users by Points Earned</h2>
                <div className="table-container">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Business Name</th>
                                <th>Email</th>
                                <th>Points Earned</th>
                                <th>Points Used</th>
                                <th>Balance</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topUsers.map((user, index) => (
                                <tr key={user.user_id}>
                                    <td>#{index + 1}</td>
                                    <td>{user.business_name}</td>
                                    <td>{user.email}</td>
                                    <td className="points-earned">+{user.points_earned}</td>
                                    <td className="points-used">-{user.points_used}</td>
                                    <td className="points-balance">{user.points_balance}</td>
                                    <td>
                                        <button
                                            className="view-btn icon-only-btn"
                                            onClick={() => fetchUserDetails(user.user_id)}
                                            title="View details"
                                            aria-label="View details"
                                        >
                                            <Eye size={15} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* User Details Modal */}
            {userDetails && (
                <div className="modal-overlay" onClick={() => setUserDetails(null)}>
                    <div className="modal-content points-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>User Points Details</h2>
                            <button className="close-btn" onClick={() => setUserDetails(null)}>×</button>
                        </div>

                        <div className="user-info">
                            <h3>{userDetails.user.business_name}</h3>
                            <p>{userDetails.user.email}</p>
                        </div>

                        <div className="points-summary">
                            <div className="summary-item">
                                <span>Earned:</span>
                                <strong>+{userDetails.points?.earned || 0}</strong>
                            </div>
                            <div className="summary-item">
                                <span>Used:</span>
                                <strong>-{userDetails.points?.used || 0}</strong>
                            </div>
                            <div className="summary-item">
                                <span>Balance:</span>
                                <strong>{userDetails.points?.balance || 0}</strong>
                            </div>
                        </div>

                        <button
                            className="adjust-btn"
                            onClick={() => setAdjustmentModal(true)}
                        >
                            Adjust Points
                        </button>

                        <div className="transactions-list">
                            <h3>Recent Transactions</h3>
                            {userDetails.transactions?.slice(0, 10).map((txn) => (
                                <div key={txn.id} className="transaction-item">
                                    <div className="txn-info">
                                        <span className="txn-type">{txn.transaction_type}</span>
                                        <span className="txn-desc">{txn.description}</span>
                                        <span className="txn-date">
                                            {formatTransactionDate(txn)}
                                        </span>
                                    </div>
                                    <span className={`txn-points ${txn.points_amount > 0 ? 'positive' : 'negative'}`}>
                                        {txn.points_amount > 0 ? '+' : ''}{txn.points_amount}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Adjustment Modal */}
            {adjustmentModal && (
                <div className="modal-overlay" onClick={() => setAdjustmentModal(false)}>
                    <div className="modal-content adjustment-modal points-adjustment-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Adjust Points</h2>
                            <button className="close-btn" onClick={() => setAdjustmentModal(false)}>×</button>
                        </div>

                        <div className="adjustment-form">
                            <div className="form-group">
                                <label>Adjustment Type</label>
                                <div className="type-buttons">
                                    <button
                                        className={`type-btn ${adjustment.type === 'add' ? 'active' : ''}`}
                                        onClick={() => setAdjustment({ ...adjustment, type: 'add' })}
                                    >
                                        <Plus size={18} />
                                        Add Points
                                    </button>
                                    <button
                                        className={`type-btn ${adjustment.type === 'remove' ? 'active' : ''}`}
                                        onClick={() => setAdjustment({ ...adjustment, type: 'remove' })}
                                    >
                                        <Minus size={18} />
                                        Remove Points
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Points Amount</label>
                                <input
                                    type="number"
                                    value={adjustment.points}
                                    onChange={(e) => setAdjustment({ ...adjustment, points: e.target.value })}
                                    placeholder="Enter points amount"
                                    min="1"
                                />
                            </div>

                            <div className="form-group">
                                <label>Reason (Required)</label>
                                <textarea
                                    value={adjustment.reason}
                                    onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                                    placeholder="Enter reason for adjustment..."
                                    rows="3"
                                />
                            </div>

                            <div className="form-actions">
                                <button className="cancel-btn" onClick={() => setAdjustmentModal(false)}>
                                    Cancel
                                </button>
                                <button className="submit-btn" onClick={handleAdjustPoints}>
                                    Confirm Adjustment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PointsAnalytics;
